import React, { useMemo, useState } from "react";
import { FiCheck, FiInfo, FiSearch } from "react-icons/fi";

// The areas the role catalogue is split into, in display order. A role's
// `area` field (written by backend/internal_script/migrate_roles.py) decides
// its section; a role with no known area lands in "Other roles".
const AREAS = [
  {
    key: "product",
    en: "Internal products (T-Global)",
    zh: "內部產品（T-Global）",
    hintEn:
      "T-Global's own catalogue: products, versions, categories, producers and change history",
    hintZh: "T-Global 自家產品目錄：產品、版本、分類、製造商與變更紀錄",
  },
  {
    key: "competitor",
    en: "Competitor products",
    zh: "競品",
    hintEn: "The competitor catalogue, its brands and categories",
    hintZh: "競品目錄、品牌與分類",
  },
  {
    key: "api",
    en: "API channels",
    zh: "API 頻道",
    hintEn:
      "Partner API keys, the sites allowed to call them and which product versions they see",
    hintZh: "合作夥伴 API 金鑰、允許呼叫的網站與可見的產品版本",
  },
  {
    key: "webchatbot",
    en: "Website chatbot",
    zh: "網站聊天機器人",
    hintEn:
      "Chatbots embedded on websites, their visitor conversations and contact requests",
    hintZh: "嵌入網站的聊天機器人、訪客對話與聯絡請求",
  },
  {
    key: "knowledge",
    en: "Agent knowledge",
    zh: "AI 知識來源",
    hintEn:
      "Content the AI assistant learns from: technical FAQs and video summaries",
    hintZh: "AI 助理的知識內容：技術常見問題與影片摘要",
  },
  {
    key: "assistant",
    en: "AI assistant",
    zh: "AI 助理",
    hintEn: "Using the AI assistant and keeping your own chat history",
    hintZh: "使用 AI 助理及管理自己的對話紀錄",
  },
  {
    key: "feedback",
    en: "Feedback review",
    zh: "回饋審核",
    hintEn: "Reviewing what users reported about the assistant's answers",
    hintZh: "審核使用者對 AI 回答的回報",
  },
  {
    key: "trend",
    en: "Trend reports",
    zh: "趨勢報告",
    hintEn: "Daily trend reports and the newsletter that sends them",
    hintZh: "每日趨勢報告與寄送報告的電子報",
  },
  {
    key: "analytics",
    en: "Analytics",
    zh: "數據分析",
    hintEn: "Usage and product inquiry statistics",
    hintZh: "使用與產品詢問統計",
  },
  {
    key: "billing",
    en: "Usage & billing",
    zh: "用量與計費",
    hintEn: "AI spend, monthly limits and their enforcement",
    hintZh: "AI 花費、每月額度與額度限制",
  },
  {
    key: "access",
    en: "Access & administration",
    zh: "權限與管理",
    hintEn: "The Admin page: users, groups, roles and the audit log",
    hintZh: "權限管理頁面：使用者、群組、角色與審計日誌",
  },
];

// Website chatbots and API channels belong to whoever created them.
export const SCOPED_AREAS = ["api", "webchatbot"];

// What a role lets someone do, read from the last part of its id. Tinted
// badges, the same style the Usage & Billing tab uses.
const KINDS = {
  view: { en: "View", zh: "檢視", className: "bg-gray-100 text-gray-700" },
  use: { en: "Use", zh: "使用", className: "bg-green-100 text-green-800" },
  change: { en: "Change", zh: "變更", className: "bg-amber-100 text-amber-800" },
  delete: { en: "Delete", zh: "刪除", className: "bg-red-100 text-red-800" },
};
const KIND_ORDER = ["view", "use", "change", "delete"];

export const kindOf = (roleId) => {
  const action = roleId.split(".").pop();
  if (["read", "view_own", "view_all", "access"].includes(action))
    return "view";
  if (action === "use") return "use";
  if (action === "delete") return "delete";
  return "change";
};

export const KindBadge = ({ kindKey, language }) => {
  const kind = KINDS[kindKey];
  return (
    <span
      className={`inline-flex justify-center w-14 px-2 py-0.5 rounded-md text-xs font-medium shrink-0 ${kind.className}`}
    >
      {language === "zh" ? kind.zh : kind.en}
    </span>
  );
};

// Roles split into AREAS order, each area's roles ordered view → use → change →
// delete. A role with no known area lands in a trailing "Other roles" section.
// Areas with no roles are left out.
export const groupRolesByArea = (roles) => {
  const byKind = (a, b) =>
    KIND_ORDER.indexOf(kindOf(a.id)) - KIND_ORDER.indexOf(kindOf(b.id)) ||
    a.id.localeCompare(b.id);
  const known = AREAS.map((area) => ({
    ...area,
    roles: roles.filter((role) => role.area === area.key).sort(byKind),
  }));
  const other = roles
    .filter((role) => !AREAS.some((area) => area.key === role.area))
    .sort(byKind);
  if (other.length) {
    known.push({
      key: "other",
      en: "Other roles",
      zh: "其他角色",
      hintEn: "Roles with no area set",
      hintZh: "尚未設定區域的角色",
      roles: other,
    });
  }
  return known.filter((section) => section.roles.length);
};

const th =
  "px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200";

export default function RolesPanel({ roles, groups = [], language }) {
  const zh = language === "zh";
  // "all", or the key of one area.
  const [selected, setSelected] = useState("all");
  const [query, setQuery] = useState("");

  const describe = (role) =>
    (zh && role.description_zh) || role.description || "";
  const groupName = (group) => group.name || group.id;
  const label = (section) => (zh ? section.zh : section.en);

  const sections = useMemo(() => groupRolesByArea(roles), [roles]);

  // Most roles first, so the columns read from most to least access.
  const columns = useMemo(
    () =>
      [...groups].sort(
        (a, b) => (b.roles || []).length - (a.roles || []).length,
      ),
    [groups],
  );
  const holds = (group, role) => (group.roles || []).includes(role.id);

  // Search narrows every area; an area whose name matches keeps all its roles.
  const q = query.trim().toLowerCase();
  const matching = sections.map((section) => {
    if (!q || [section.en, section.zh].some((t) => t.toLowerCase().includes(q)))
      return section;
    return {
      ...section,
      roles: section.roles.filter((role) =>
        [role.id, role.description, role.description_zh].some((t) =>
          (t || "").toLowerCase().includes(q),
        ),
      ),
    };
  });
  const shown = matching.filter(
    (section) =>
      section.roles.length && (selected === "all" || section.key === selected),
  );
  const inView = shown.flatMap((section) => section.roles);
  const current = sections.find((section) => section.key === selected);
  const matchCount = matching.reduce((n, section) => n + section.roles.length, 0);

  const onSearch = (value) => {
    setQuery(value);
    // Results can sit in any area, so a search always looks across all of them.
    if (value.trim()) setSelected("all");
  };

  const categorised = sections.filter((section) => section.key !== "other");
  const roleCount = categorised.reduce((n, s) => n + s.roles.length, 0);

  if (roles.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-12 text-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {zh ? "無角色" : "No Roles Found"}
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          {zh
            ? "無法載入角色，請重新整理或聯繫技術支持。"
            : "Roles could not be loaded. Refresh the page or contact tech support."}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-3 py-1.5 rounded-lg text-sm font-medium text-orange-600 border border-orange-200 hover:bg-orange-50"
        >
          {zh ? "刷新" : "Refresh"}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold text-gray-900">
              {zh ? "角色和權限" : "Roles and Authorities"}
            </h2>
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              {zh
                ? `${roleCount} 個角色・${categorised.length} 個區域`
                : `${roleCount} roles · ${categorised.length} areas`}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {zh
              ? "群組透過組合角色來決定成員可以做什麼。選擇左側的區域，查看每個角色的用途與擁有它的群組。"
              : "A group's roles decide what its members can do. Pick an area to see what each role does and which groups hold it."}
          </p>
        </div>
        <div className="relative">
          <FiSearch
            className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2"
            aria-hidden="true"
          />
          <input
            value={query}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={zh ? "搜尋角色…" : "Search roles…"}
            className="w-full md:w-72 border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
          />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col md:flex-row">
        {/* Area list */}
        <nav className="md:w-64 shrink-0 p-3 flex md:flex-col gap-1 overflow-x-auto no-scrollbar border-b md:border-b-0 md:border-r border-gray-200">
          {[
            { key: "all", en: "All areas", zh: "全部區域", count: matchCount },
            ...matching.map((section) => ({
              ...section,
              count: section.roles.length,
            })),
          ].map((item) => {
            const active = selected === item.key;
            return (
              <button
                key={item.key}
                onClick={() => setSelected(item.key)}
                className={`shrink-0 md:w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                  active
                    ? "bg-orange-50 text-orange-700 font-medium"
                    : item.count
                      ? "text-gray-700 hover:bg-gray-50"
                      : "text-gray-300"
                }`}
              >
                <span className="truncate">{label(item)}</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    active
                      ? "bg-orange-100 text-orange-700"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {item.count}
                </span>
              </button>
            );
          })}
        </nav>

        {/* Roles in the selected area, one column per group */}
        <div className="flex-1 min-w-0">
          <div className="px-5 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {selected === "all" || !current
                ? zh
                  ? "全部區域"
                  : "All areas"
                : label(current)}
            </h3>
            <p className="text-sm text-gray-500">
              {selected === "all" || !current
                ? zh
                  ? "所有角色，依區域分組。點選區域名稱可只看該區域。"
                  : "Every role, grouped by area. Click an area's name to see only that area."
                : zh
                  ? current.hintZh
                  : current.hintEn}
            </p>
          </div>

          {SCOPED_AREAS.includes(selected) && (
            <div className="mx-5 mt-4 flex gap-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
              <FiInfo className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                {zh ? (
                  <>
                    <code className="font-mono">view_own</code> 與{" "}
                    <code className="font-mono">view_all</code>{" "}
                    決定成員能存取哪些項目：僅限自己建立的，或全部。其他角色決定能對這些項目做什麼。
                  </>
                ) : (
                  <>
                    <code className="font-mono">view_own</code> and{" "}
                    <code className="font-mono">view_all</code> decide{" "}
                    <strong>which</strong> ones a member can reach: only those
                    they created, or all of them. The other roles decide{" "}
                    <strong>what</strong> they can do there.
                  </>
                )}
              </span>
            </div>
          )}

          {shown.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-gray-500">
              {q
                ? zh
                  ? `這個區域沒有符合「${query}」的角色`
                  : `No roles here match "${query}"`
                : zh
                  ? "這個區域沒有角色"
                  : "No roles in this area"}
            </p>
          ) : (
            <div
              className={`overflow-auto no-scrollbar max-h-[70vh] ${SCOPED_AREAS.includes(selected) ? "mt-4 border-t border-gray-200" : ""}`}
            >
              <table className="min-w-full border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th
                      className={`${th} sticky top-0 left-0 z-20 text-left min-w-[20rem]`}
                    >
                      {zh ? "角色" : "Role"}
                    </th>
                    {columns.map((group) => (
                      <th
                        key={group.id}
                        className={`${th} sticky top-0 z-10 text-center whitespace-nowrap`}
                      >
                        {groupName(group)}
                        <div className="mt-0.5 font-normal normal-case tracking-normal text-gray-400 tabular-nums">
                          {inView.filter((role) => holds(group, role)).length}/
                          {inView.length}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {shown.map((section) => (
                    <React.Fragment key={section.key}>
                      {selected === "all" && (
                        <tr>
                          <td className="sticky left-0 z-[5] bg-white border-b border-gray-200 px-4 pt-5 pb-2">
                            <button
                              onClick={() => setSelected(section.key)}
                              className="text-sm font-semibold text-gray-900 hover:text-orange-600 hover:underline"
                            >
                              {label(section)}
                            </button>
                          </td>
                          {columns.map((group) => (
                            <td
                              key={group.id}
                              className="bg-white border-b border-gray-200 px-4 pt-5 pb-2 text-center text-xs text-gray-400 tabular-nums"
                            >
                              {section.roles.filter((role) => holds(group, role)).length}/
                              {section.roles.length}
                            </td>
                          ))}
                        </tr>
                      )}
                      {section.roles.map((role) => (
                        <tr key={role.id} className="group">
                          <td className="sticky left-0 z-[5] bg-white group-hover:bg-gray-50 border-b border-gray-200 px-4 py-3">
                            <div className="flex items-start gap-3">
                              <KindBadge kindKey={kindOf(role.id)} language={language} />
                              <div className="min-w-0">
                                <p className="text-sm text-gray-700">
                                  {describe(role) || (
                                    <span className="italic text-gray-400">
                                      {zh ? "尚無說明" : "No description"}
                                    </span>
                                  )}
                                </p>
                                <code className="text-xs text-gray-400 font-mono">
                                  {role.id}
                                </code>
                              </div>
                            </div>
                          </td>
                          {columns.map((group) => (
                            <td
                              key={group.id}
                              className="border-b border-gray-200 px-4 py-3 text-center group-hover:bg-gray-50"
                            >
                              {holds(group, role) ? (
                                <FiCheck
                                  className="w-4 h-4 mx-auto text-green-600"
                                  aria-label={zh ? "擁有" : "has this role"}
                                />
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
